import type { ExpertWorkspaceWebSocket } from "@expert/websocket";

import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { getAmazonConnectLoginPopupConfig } from "@expert/amazon-connect-utils";
import { getAppConfigSingleton } from "@expert/app-config";
import { sendMetrics } from "@expert/cloudwatch-metrics";
import { setFullStoryUserProperties } from "@expert/monitoring";
import { getAmazonConnectLoginUrl, type ServiceProvider, selectPartnerWithOverride } from "@expert/service-providers";

import type { ChatEventCallbacks, ChatMessagesCache } from "@/sdk";
import type { AgentSdkBase } from "@/sdk/agent/agentSdkBase";

import { getServiceProviderAccessToken } from "@/auth";
import { env } from "@/config";
import { userCache } from "@/shared-utils";

const IS_DEV = import.meta.env.VITE_ENV_TYPE === "development";

/** Provider-agnostic parameters required to create the orchestrated agent SDK. */
export interface CreateSdkParams {
    /** Function to create HTML containers for provider UIs (e.g., Amazon Connect CCP) */
    createContainer: (name: string) => Promise<HTMLElement>;
    employeeNum: string | null;
    serviceProvider: ServiceProvider;
    userFriendlyName?: string;
    webSocket: ExpertWorkspaceWebSocket;
    /** Cache for storing and retrieving chat messages across providers */
    chatMessagesCache: ChatMessagesCache;
    /** Universal chat event callbacks - handles notifications, analytics, etc. */
    chatEventCallbacks: ChatEventCallbacks;
    onAuthRedirect?: VoidFunction;
}

/**
 * Factory functions for creating provider-specific agent SDKs
 */
type AgentSdkFactory = {
    [key in ServiceProvider]: (createSdkParams: CreateSdkParams) => Promise<AgentSdkBase>;
};

const agentSdkFactory: AgentSdkFactory = {
    AMAZON_CONNECT: async function create({
        createContainer,
        employeeNum,
        onAuthRedirect,
        chatEventCallbacks,
        chatMessagesCache,
    }: CreateSdkParams) {
        const { amazonConnectInstancesInfo, allowedPartners, getPartnersConfig } = getAppConfigSingleton();
        if (!amazonConnectInstancesInfo) throw new Error("amazonConnectInstancesInfo is not defined");

        // TODO: allow multi-partner configuration.
        const partner = selectPartnerWithOverride(allowedPartners[0]);
        const amazonConnectInstancesInfoConfigKey = getPartnersConfig(partner)?.amazonConnectInstancesInfoConfigKey;
        const partnerWithOrg = userCache.org ? `${partner}:${userCache.org}` : partner;

        let connectInstanceInfo = amazonConnectInstancesInfo["others"];
        if (amazonConnectInstancesInfoConfigKey && amazonConnectInstancesInfoConfigKey in amazonConnectInstancesInfo) {
            connectInstanceInfo = amazonConnectInstancesInfo[amazonConnectInstancesInfoConfigKey];
        } else if (partnerWithOrg in amazonConnectInstancesInfo) {
            connectInstanceInfo = amazonConnectInstancesInfo[partnerWithOrg];
        }

        const { instanceAlias, instanceId, region, identityProvider, secondaryInstanceAlias } = connectInstanceInfo;

        const ccpUrl = `https://${instanceAlias}.my.connect.aws/connect/ccp-v2`;
        const secondaryCCPUrl = secondaryInstanceAlias
            ? `https://${secondaryInstanceAlias}.my.connect.aws/connect/ccp-v2`
            : undefined;

        const context = {
            environment: import.meta.env.VITE_ENV_TYPE as string,
            channel: env.channel || "voice",
            employeeId: employeeNum ?? "",
        };
        const [isGlobalAuthEnabled, enableGlobalResiliency] = await Promise.all([
            getFeature<boolean>("EWP-isAmazonConnectGlobalAuthEnabled", context),
            getFeature<boolean>("EWP-isAmazonConnectGlobalResiliencyEnabled", context),
        ]);

        const { default: AmazonConnectAgentSDK } = await import("../amazon-connect/AmazonConnectAgentSDK");

        const container = await createContainer(`Amazon Connect Contact Control Panel: ${instanceAlias}`);

        const { loginPopup: loginPopupFromConfig, loginPopupAutoClose } = await getAmazonConnectLoginPopupConfig(
            "ExWo",
            context,
        );
        const isHttps = globalThis.location.protocol === "https:";
        const loginPopup = loginPopupFromConfig || !isHttps;
        const destinationRelay = loginPopup ? undefined : globalThis.location.href;
        const loginUrl =
            identityProvider &&
            getAmazonConnectLoginUrl(identityProvider, region, instanceId, { destinationRelay, isGlobalAuthEnabled });

        setFullStoryUserProperties({
            connectInstanceAlias: instanceAlias,
            partner,
            isAmazonConnectGlobalAuthEnabled: isGlobalAuthEnabled,
            isAmazonConnectGlobalResiliencyEnabled: enableGlobalResiliency,
            amazonConnectLoginMethod: loginPopup ? "popup" : "redirect",
        });

        const result = await AmazonConnectAgentSDK.create(container, ccpUrl, partner, employeeNum, {
            secondaryCCPUrl,
            enableGlobalResiliency,
            loginUrl,
            loginPopup,
            loginPopupAutoClose,
            metricsHandler: sendMetrics,
            onAuthRedirect,
            connectRegion: region,
            // Only pass chat dependencies when chat channel is enabled
            ...(env.channel === "chat"
                ? {
                      chatDependencies: { chatEventCallbacks, chatMessagesCache },
                  }
                : {}),
        });

        return result;
    },
    TWILIO: async ({ employeeNum, userFriendlyName, webSocket, chatMessagesCache, chatEventCallbacks }) => {
        const { TwilioAgentSDK } = await import("../twilio/twilioAgentSdk");

        return await TwilioAgentSDK.create(
            () => getServiceProviderAccessToken(userFriendlyName),
            employeeNum,
            chatMessagesCache,
            chatEventCallbacks,
            webSocket,
            userFriendlyName,
        );
    },
    MOCK_PROVIDER: async ({ employeeNum }: CreateSdkParams) => {
        if (!IS_DEV) throw new Error("MOCK_PROVIDER is only available in the development environment");
        if (!employeeNum) throw new Error("employeeNum must be provided to MOCK_PROVIDER");

        const { MockSdk } = await import("@/mock-sdk/MockSdk");

        return new MockSdk({ employeeNum });
    },
    EXPERT_UNIVERSITY: async () => {
        if (env.channel === "chat") {
            throw new Error("EXPERT_UNIVERSITY is not available for chat channel");
        }

        const { default: ExpertUniversitySdk } = await import("../expert-university/ExpertUniversitySdk");
        return new ExpertUniversitySdk();
    },
};

/**
 * Initializes all specified service providers and combines them
 * into a single orchestrated SDK instance that supports multiple providers.
 */
export async function createAgentSDK(params: CreateSdkParams) {
    return await agentSdkFactory[params.serviceProvider](params);
}
